/** The API token lifetime and the user's inactivity deadline are separate clocks. */
export const SESSION_IDLE_TIMEOUT_MS = 120 * 60 * 1000;
export const SESSION_REFRESH_INTERVAL_MS = 15 * 60 * 1000;
export const SESSION_REFRESH_RETRY_MS = 60 * 1000;
export const SESSION_STORAGE_PREFIX = 'bips:session:v2:';

export interface SessionIdentity {
  baseURL: string;
  userId: number | string;
}

export interface SessionTicket extends SessionIdentity {
  sessionId: string;
  generation: number;
  token: string;
}

export interface SessionRecord extends SessionTicket {
  status: 'active' | 'signed-out';
  startedAt: number;
  timeoutMinutes: number;
  expiresAt: number;
  nextRefreshAt: number;
  reason?: string;
}

export interface SessionCredentials extends SessionIdentity {
  token: string;
  timeout?: unknown;
  /** Used only when adopting a session created before the coordinator existed. */
  lastActivity?: number;
}

export interface SessionStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface SessionLocks {
  request<T>(
    name: string,
    options: { mode: 'shared' | 'exclusive' },
    callback: () => Promise<T>
  ): Promise<T>;
}

export interface RenewalResult {
  status: number;
  token?: string;
  timeout?: unknown;
}

export interface SessionCoordinatorOptions {
  storage?: SessionStorage | null;
  locks?: SessionLocks | null;
  renew(ticket: SessionTicket): Promise<RenewalResult>;
  onChange?(session: SessionRecord): void;
  now?: () => number;
  createId?: () => string;
}

export class SessionUnavailableError extends Error {
  readonly code: 'SESSION_UNAVAILABLE' | 'COSNETICS_AUTHENTICATION_REQUIRED';

  constructor(public readonly reason: string) {
    super(
      reason === 'coordination-unavailable'
        ? 'Your browser cannot keep this session synchronized. Enable site storage and use an up-to-date browser.'
        : reason === 'renewal-unavailable'
        ? 'Your session could not be renewed. Check your connection and try again shortly.'
        : 'Your session has ended. Please sign in again.'
    );
    this.name = 'SessionUnavailableError';
    this.code = reason === 'coordination-unavailable' || reason === 'renewal-unavailable'
      ? 'SESSION_UNAVAILABLE'
      : 'COSNETICS_AUTHENTICATION_REQUIRED';
  }
}

export { SessionUnavailableError as SessionError };

export const normalizeBaseURL = (baseURL: string): string => {
  const url = new URL(baseURL);
  url.hash = '';
  url.search = '';
  url.pathname = `${url.pathname.replace(/\/+$/, '')}/`;
  return url.toString();
};

export const sessionStorageKey = (identity: SessionIdentity): string =>
  `${SESSION_STORAGE_PREFIX}${JSON.stringify([
    normalizeBaseURL(identity.baseURL),
    String(identity.userId)
  ])}`;

const activityKey = (session: SessionRecord): string =>
  `${sessionStorageKey(session)}:activity:${session.sessionId}`;

const sameTicket = (session: SessionRecord | null, ticket: SessionTicket): session is SessionRecord =>
  Boolean(
    session &&
      session.status === 'active' &&
      session.sessionId === ticket.sessionId &&
      session.generation === ticket.generation &&
      session.token === ticket.token
  );

const tokenLifetime = (timeout: unknown, fallback = 15): number => {
  const minutes = Number(timeout);
  return Number.isFinite(minutes) && minutes > 0 ? minutes : fallback;
};

const refreshDelay = (minutes: number): number => {
  const lifetime = minutes * 60 * 1000;
  return Math.min(SESSION_REFRESH_INTERVAL_MS, lifetime - Math.min(60 * 1000, lifetime * 0.2));
};

/**
 * Web Locks cover the entire protected operation, including GraphQL work in the
 * middleman. Renewal takes the exclusive operation lock, so it cannot invalidate
 * a token while one of our tabs still has an operation using it. A separate state
 * lock permits logout/401 handling while an operation is still outstanding.
 */
export class SessionCoordinator {
  private readonly now: () => number;
  private readonly createId: () => string;
  private readonly attached = new Set<string>();

  constructor(private readonly options: SessionCoordinatorOptions) {
    this.now = options.now || Date.now;
    this.createId = options.createId || (() => crypto.randomUUID());
  }

  private available(): void {
    if (!this.options.storage || !this.options.locks) {
      throw new SessionUnavailableError('coordination-unavailable');
    }
  }

  private read(key: string): string | null {
    this.available();
    try {
      return this.options.storage!.getItem(key);
    } catch {
      throw new SessionUnavailableError('coordination-unavailable');
    }
  }

  private write(key: string, value: string): void {
    this.available();
    try {
      this.options.storage!.setItem(key, value);
    } catch {
      throw new SessionUnavailableError('coordination-unavailable');
    }
  }

  private lock<T>(identity: SessionIdentity, kind: 'state' | 'operations', mode: 'shared' | 'exclusive', work: () => Promise<T>): Promise<T> {
    this.available();
    return this.options.locks!.request(`${sessionStorageKey(identity)}:${kind}`, { mode }, work);
  }

  private publish(session: SessionRecord): SessionRecord {
    this.options.onChange?.(session);
    return session;
  }

  private save(session: SessionRecord): SessionRecord {
    this.write(sessionStorageKey(session), JSON.stringify(session));
    return this.publish(session);
  }

  get(identity: SessionIdentity): SessionRecord | null {
    const raw = this.read(sessionStorageKey(identity));
    if (!raw) return null;
    try {
      const record = JSON.parse(raw) as SessionRecord;
      if (
        !record ||
        typeof record.sessionId !== 'string' ||
        typeof record.token !== 'string' ||
        !Number.isFinite(record.generation) ||
        !Number.isFinite(record.startedAt) ||
        !Number.isFinite(record.expiresAt) ||
        !Number.isFinite(record.nextRefreshAt) ||
        !Number.isFinite(record.timeoutMinutes) ||
        record.timeoutMinutes <= 0 ||
        (record.status !== 'active' && record.status !== 'signed-out') ||
        (record.status === 'active' && !record.token) ||
        sessionStorageKey(record) !== sessionStorageKey(identity)
      ) throw new Error('Invalid session record');
      return record;
    } catch {
      // A malformed shared record must never be replaced by an old tab snapshot.
      throw new SessionUnavailableError('coordination-unavailable');
    }
  }

  private lastActivity(session: SessionRecord): number {
    const stored = Number(this.read(activityKey(session)));
    return Number.isFinite(stored) && stored >= session.startedAt
      ? Math.min(stored, this.now())
      : session.startedAt;
  }

  private end(session: SessionRecord, reason: string): SessionRecord {
    return this.save({ ...session, status: 'signed-out', token: '', generation: session.generation + 1, expiresAt: 0, reason });
  }

  /** Must be called under the state lock. Checks the deadline before new activity. */
  private current(identity: SessionIdentity): SessionRecord | null {
    const session = this.get(identity);
    if (session?.status === 'active' && this.now() - this.lastActivity(session) >= SESSION_IDLE_TIMEOUT_MS) {
      return this.end(session, 'idle-timeout');
    }
    return session;
  }

  private create(credentials: SessionCredentials, adopting: boolean): SessionRecord {
    if (!credentials.token.trim()) throw new SessionUnavailableError('missing-token');
    const now = this.now();
    const timeoutMinutes = tokenLifetime(credentials.timeout);
    const startedAt = adopting && credentials.lastActivity != null && Number.isFinite(credentials.lastActivity) && credentials.lastActivity >= 0
      ? Math.min(now, credentials.lastActivity)
      : now;
    const session: SessionRecord = {
      baseURL: normalizeBaseURL(credentials.baseURL),
      userId: credentials.userId,
      sessionId: this.createId(),
      generation: 0,
      token: credentials.token,
      status: 'active',
      startedAt,
      timeoutMinutes,
      // A snapshot does not establish when its token was issued. Validate it by
      // renewing before admitting any protected request.
      expiresAt: adopting ? now : now + timeoutMinutes * 60 * 1000,
      nextRefreshAt: adopting ? now : now + refreshDelay(timeoutMinutes)
    };
    this.write(activityKey(session), String(startedAt));
    this.attached.add(sessionStorageKey(session));
    return this.save(session);
  }

  /** Only a successful explicit login may replace a signed-out record. */
  async start(credentials: SessionCredentials, isCurrent: () => boolean = () => true): Promise<SessionRecord> {
    return this.lock(credentials, 'state', 'exclusive', async () => {
      if (!isCurrent()) throw new SessionUnavailableError('session-changed');
      return this.create(credentials, false);
    });
  }

  /** Serialize a known account's login POST with requests and token renewal. */
  async replace(identity: SessionIdentity, authenticate: () => Promise<{ token: string; timeout?: unknown }>, isCurrent: () => boolean = () => true): Promise<SessionRecord> {
    return this.lock(identity, 'operations', 'exclusive', async () => {
      if (!isCurrent()) throw new SessionUnavailableError('session-changed');
      const before = this.get(identity);
      const credentials = await authenticate();
      return this.lock(identity, 'state', 'exclusive', async () => {
        const current = this.get(identity);
        if (
          !isCurrent() ||
          current?.sessionId !== before?.sessionId ||
          current?.generation !== before?.generation ||
          current?.status !== before?.status
        ) throw new SessionUnavailableError('session-changed');
        return this.create({ ...identity, ...credentials }, false);
      });
    });
  }

  async attach(credentials: SessionCredentials): Promise<SessionRecord | null> {
    return this.lock(credentials, 'state', 'exclusive', async () => {
      const existing = this.current(credentials);
      const key = sessionStorageKey(credentials);
      if (existing) {
        this.attached.add(key);
        return this.publish(existing);
      }
      if (this.attached.has(key)) throw new SessionUnavailableError('missing-session');
      return this.create(credentials, true);
    });
  }

  async sync(identity: SessionIdentity): Promise<SessionRecord | null> {
    return this.lock(identity, 'state', 'exclusive', async () => {
      const session = this.current(identity);
      return session ? this.publish(session) : null;
    });
  }

  async activity(identity: SessionIdentity): Promise<void> {
    await this.lock(identity, 'state', 'exclusive', async () => {
      const session = this.current(identity);
      if (session?.status === 'active') this.write(activityKey(session), String(this.now()));
    });
  }

  async check(identity: SessionIdentity): Promise<void> {
    const initial = await this.sync(identity);
    if (initial?.status !== 'active' || this.now() < initial.nextRefreshAt) return;

    await this.lock(identity, 'operations', 'exclusive', async () => {
      const session = await this.sync(identity);
      // Another tab may have renewed while this tab waited for the lock.
      if (session?.status !== 'active' || this.now() < session.nextRefreshAt) return;
      let result: RenewalResult | undefined;
      const requestedAt = this.now();
      try {
        result = await this.options.renew(session);
      } catch (error) {
        const status = (error as { response?: { status?: number } })?.response?.status;
        if (status === 401 || status === 403) result = { status };
      }
      await this.lock(identity, 'state', 'exclusive', async () => {
        const current = this.current(identity);
        if (!sameTicket(current, session)) return;
        // A rejection from the renewal endpoint requires a fresh sign-in.
        // Other endpoints can use 403 for permissions without ending a session.
        if (result?.status === 401 || result?.status === 403) {
          this.end(current, 'unauthorized');
        } else if (result?.status === 200 && typeof result.token === 'string' && result.token.trim()) {
          const timeoutMinutes = tokenLifetime(result.timeout, current.timeoutMinutes);
          this.save({
            ...current,
            token: result.token,
            generation: current.generation + 1,
            timeoutMinutes,
            expiresAt: requestedAt + timeoutMinutes * 60 * 1000,
            nextRefreshAt: requestedAt + refreshDelay(timeoutMinutes)
          });
        } else {
          // Retry network failures and malformed success responses. Never extend
          // the old token's expiry until a replacement was actually received.
          this.save({ ...current, nextRefreshAt: this.now() + SESSION_REFRESH_RETRY_MS });
        }
      });
    });
  }

  async withRequest<T>(identity: SessionIdentity, request: (ticket: SessionTicket) => Promise<T>): Promise<T> {
    for (;;) {
      await this.check(identity);
      const outcome = await this.lock(identity, 'operations', 'shared', async () => {
        const session = await this.sync(identity);
        if (!session || session.status !== 'active') {
          throw new SessionUnavailableError(session?.reason || 'missing-session');
        }
        // The deadline may have arrived while acquiring the shared lock. Release
        // it and renew exclusively instead of making a request with an old token.
        if (this.now() >= session.nextRefreshAt) return { retry: true as const };
        if (this.now() >= session.expiresAt) throw new SessionUnavailableError('renewal-unavailable');
        return { retry: false as const, result: await request(session) };
      });
      if (!outcome.retry) return outcome.result;
    }
  }

  /** Old 401s cannot invalidate a replacement token or a subsequent login. */
  async reject(ticket: SessionTicket): Promise<boolean> {
    return this.lock(ticket, 'state', 'exclusive', async () => {
      const current = this.current(ticket);
      if (!sameTicket(current, ticket)) return false;
      this.end(current, 'unauthorized');
      return true;
    });
  }

  async logout(
    identity: SessionIdentity,
    expected?: Pick<SessionTicket, 'sessionId' | 'generation'>,
    legacy?: { token: string; timeout?: unknown }
  ): Promise<void> {
    // Capture before waiting: a queued logout must not kill a subsequent login.
    const before = expected || this.get(identity);
    await this.lock(identity, 'state', 'exclusive', async () => {
      const current = this.get(identity);
      if (!current) {
        // Logout may precede the first app-effect attachment after upgrading.
        // Leave a tombstone so a one-time snapshot cannot recreate that session.
        this.save({
          baseURL: normalizeBaseURL(identity.baseURL),
          userId: identity.userId,
          sessionId: this.createId(),
          generation: 0,
          token: '',
          status: 'signed-out',
          startedAt: this.now(),
          timeoutMinutes: tokenLifetime(legacy?.timeout),
          expiresAt: 0,
          nextRefreshAt: 0,
          reason: 'logout'
        });
        return;
      }
      if (
        current.status === 'active' &&
        (current.sessionId === before?.sessionId || (!before && legacy?.token === current.token))
      ) this.end(current, 'logout');
    });
  }
}
